/**
 * Shared utility functions for domain management
 */
import { getConfigValue } from "./runtimeConfig";

// Storage key constants
export const DOMAINS_STORAGE_KEY = "authhero_domains";
export const SELECTED_DOMAIN_STORAGE_KEY = "authhero_selected_domain";

// Connection method types
export type ConnectionMethod = "login" | "token" | "client_credentials";

// Domain configuration interface
export interface DomainConfig {
  url: string;
  connectionMethod: ConnectionMethod;
  // Login method fields
  clientId?: string;
  restApiUrl?: string;
  // Token method field
  token?: string;
  // Client credentials method fields
  clientSecret?: string;
  // Send tenant-scoped management API calls to `{tenant_id}.{apiHost}`
  // instead of the apex host. Requires the deployment to route tenant
  // subdomains (wildcard DNS) to the auth server. The `tenant-id` header is
  // still sent for backward compatibility with older servers.
  useTenantSubdomains?: boolean;
}

/**
 * Formats a domain string (removes protocol if present)
 */
export const formatDomain = (domain: string): string => {
  return domain.trim().replace(/^https?:\/\//, "");
};

/**
 * Gets default domain configuration from environment variables
 */
const getDefaultDomainFromEnv = (): DomainConfig | null => {
  const domain = getConfigValue("domain");
  const clientId = getConfigValue("clientId");
  const apiUrl = getConfigValue("apiUrl");

  if (!domain) {
    return null;
  }

  return {
    url: formatDomain(domain),
    connectionMethod: "login",
    clientId: clientId || undefined,
    restApiUrl: apiUrl || undefined,
    // Only set when enabled — this object is spread over stored configs in
    // getDomainFromStorage, and an explicit `undefined` would clobber a
    // user-enabled flag.
    ...(getConfigValue("useTenantSubdomains") === "true"
      ? { useTenantSubdomains: true }
      : {}),
  };
};

/**
 * Gets domains from localStorage
 * Handles both formats (array of objects or array of strings) for backward compatibility
 * Includes default domain from environment variables if not already in storage
 */
export const getDomainFromStorage = (): DomainConfig[] => {
  try {
    const storedValue = localStorage.getItem(DOMAINS_STORAGE_KEY);
    let domains: DomainConfig[] = [];

    if (storedValue) {
      const parsedData = JSON.parse(storedValue);

      // Handle both formats: array of objects with url property or array of strings
      if (Array.isArray(parsedData)) {
        domains = parsedData
          .filter((item) => item !== null && item !== undefined)
          .map((item) => {
            if (typeof item === "object" && item !== null && "url" in item) {
              // Add connectionMethod if it doesn't exist (for backward compatibility)
              if (!("connectionMethod" in item)) {
                return {
                  ...item,
                  connectionMethod: "login" as ConnectionMethod, // Assume login for existing entries
                } as DomainConfig;
              }
              return item as DomainConfig;
            } else {
              // Convert string domains to DomainConfig format (backward compatibility)
              return {
                url: String(item),
                connectionMethod: "login" as ConnectionMethod,
                clientId: "", // Empty clientId for legacy entries
              };
            }
          })
          .filter((domain) => domain.url.trim() !== ""); // Remove empty domains
      }
    }

    // Add or update default domain from environment if configured
    const defaultDomain = getDefaultDomainFromEnv();
    if (defaultDomain) {
      const existingIndex = domains.findIndex(
        (d) => formatDomain(d.url) === defaultDomain.url,
      );

      if (existingIndex >= 0) {
        // Update existing domain with environment config (env takes precedence)
        domains[existingIndex] = {
          ...domains[existingIndex],
          ...defaultDomain,
        };
      } else {
        // Add new domain at the beginning
        domains.unshift(defaultDomain);
      }
    }

    return domains;
  } catch (e) {
    console.error("Failed to parse domains from localStorage", e);
    // Return default domain from env if storage fails
    const defaultDomain = getDefaultDomainFromEnv();
    return defaultDomain ? [defaultDomain] : [];
  }
};

/**
 * Saves domains to localStorage
 */
export const saveDomainToStorage = (domains: DomainConfig[]): void => {
  try {
    localStorage.setItem(DOMAINS_STORAGE_KEY, JSON.stringify(domains));
  } catch (e) {
    console.error("Failed to save domains to localStorage", e);
  }
};

/**
 * Gets the selected domain from localStorage
 * Falls back to first domain in storage if no selected domain is set
 */
export const getSelectedDomainFromStorage = (): string => {
  try {
    const selectedDomain = localStorage.getItem(SELECTED_DOMAIN_STORAGE_KEY);
    if (selectedDomain) return selectedDomain;

    // Fallback to first domain in storage if no selected domain
    const domains = getDomainFromStorage();
    return domains[0]?.url || "";
  } catch (e) {
    console.error("Failed to get selected domain from localStorage", e);
    return "";
  }
};

/**
 * Saves the selected domain to localStorage
 */
export const saveSelectedDomainToStorage = (domain: string): void => {
  try {
    localStorage.setItem(SELECTED_DOMAIN_STORAGE_KEY, domain);
  } catch (e) {
    console.error("Failed to save selected domain to localStorage", e);
  }
};

/**
 * Gets client ID for a specific domain
 * Falls back to environment variable if not found
 */
export const getClientIdFromStorage = (domain: string): string => {
  // Ensure domain is properly formatted for comparison
  const formattedDomain = formatDomain(domain);

  const domains = getDomainFromStorage();

  // Look for matching domain in the array
  for (const d of domains) {
    if (d.url === formattedDomain && d.clientId) {
      return d.clientId;
    }
  }

  // Fallback to runtime/environment config
  return getConfigValue("clientId");
};

/**
 * Constructs a full URL with the appropriate protocol
 * - Preserves an explicit http:// for loopback hosts; upgrades it to https://
 *   for everything else
 * - Without a scheme, a domain matching the page's own host follows the page
 *   protocol (the bundled admin UI is always same-origin with its API), and
 *   any other host gets https:// — local templates serve TLS, and plain-http
 *   servers can still be reached with an explicit http:// URL
 */
export const buildUrlWithProtocol = (domain: string): string => {
  const trimmedDomain = domain.trim();

  const isLoopback = (hostname: string) =>
    hostname === "localhost" || hostname === "127.0.0.1";

  // Parse the input to extract the hostname reliably
  const hasScheme = /^https?:\/\//i.test(trimmedDomain);
  const toParse = hasScheme ? trimmedDomain : `https://${trimmedDomain}`;

  let parsed: URL;
  try {
    parsed = new URL(toParse);
  } catch {
    // If it can't be parsed, fall back to https
    return `https://${trimmedDomain}`;
  }

  if (hasScheme) {
    // Allow http only for loopback; upgrade everything else to https
    if (parsed.protocol === "http:" && !isLoopback(parsed.hostname)) {
      return trimmedDomain.replace(/^http:\/\//i, "https://");
    }
    return trimmedDomain;
  }

  // No scheme provided — same-origin domains follow the page protocol
  if (typeof window !== "undefined" && parsed.host === window.location.host) {
    return `${window.location.protocol}//${trimmedDomain}`;
  }

  return `https://${trimmedDomain}`;
};

// A tenant id can only become a subdomain when it is a valid DNS label.
const DNS_LABEL_REGEX = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * Derives the per-tenant management API base URL by prefixing the tenant id
 * as a subdomain: `https://api.example.com` → `https://kvartal.api.example.com`.
 *
 * Returns `null` when the URL can't take a tenant subdomain — loopback hosts
 * (local dev keeps using the apex + `tenant-id` header), IP addresses, or a
 * tenant id that isn't a valid DNS label. Callers should fall back to the
 * apex URL in that case.
 */
export const deriveTenantSubdomainUrl = (
  baseUrl: string,
  tenantId: string,
): string | null => {
  const label = tenantId.trim().toLowerCase();
  if (!DNS_LABEL_REGEX.test(label)) return null;

  let parsed: URL;
  try {
    parsed = new URL(baseUrl);
  } catch {
    return null;
  }

  const host = parsed.hostname;
  if (host === "localhost" || host === "127.0.0.1") return null;
  // IPv4 and (bracketed) IPv6 hosts can't take subdomains
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.includes(":")) return null;

  parsed.hostname = `${label}.${host}`;
  return parsed.toString();
};
